import { useRef, useEffect } from 'react';

// Face image paths: front, back, right, left, top, bottom
const FACE_IMAGES = [
  '/assets/kuutio-faces/teknologia.png',
  '/assets/kuutio-faces/politiikka.png',
  '/assets/kuutio-faces/talous.png',
  '/assets/kuutio-faces/luonto_ilmasto.png',
  '/assets/kuutio-faces/uskomukset_arvot.png',
  '/assets/kuutio-faces/kayttaytyminen.png',
];

// Vertex shader: full-screen triangle
const VS = `
attribute vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Fragment shader: raymarched rounded-corner cube with face textures
const FS = `
precision highp float;

uniform float uTime;
uniform float uDuration;
uniform vec2 uRes;
uniform sampler2D uF0, uF1, uF2, uF3, uF4, uF5;

float sdRoundBox(vec3 p, vec3 b, float r) {
  vec3 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0) - r;
}

mat3 mRotX(float a) {
  float c = cos(a), s = sin(a);
  return mat3(1,0,0, 0,c,s, 0,-s,c);
}
mat3 mRotY(float a) {
  float c = cos(a), s = sin(a);
  return mat3(c,0,-s, 0,1,0, s,0,c);
}
mat3 mRotZ(float a) {
  float c = cos(a), s = sin(a);
  return mat3(c,s,0, -s,c,0, 0,0,1);
}

mat3 transp(mat3 m) {
  return mat3(
    m[0][0], m[1][0], m[2][0],
    m[0][1], m[1][1], m[2][1],
    m[0][2], m[1][2], m[2][2]
  );
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uRes * 0.5) / min(uRes.x, uRes.y);

  vec3 ro = vec3(0.0, 0.0, 2.8);
  vec3 rd = normalize(vec3(uv, -1.2));

  // Start from isometric angle, then tumble
  float phase = uTime * 6.2832 / uDuration;
  float rx = -0.6155 + phase;
  float ry = 0.7854 + phase;
  float rz = sin(phase) * 0.785;

  mat3 rot = mRotX(rx) * mRotY(ry) * mRotZ(rz);
  mat3 invRot = transp(rot);

  vec3 oro = invRot * ro;
  vec3 ord = invRot * rd;

  vec3 boxSize = vec3(0.5);
  float cornerR = 0.12;
  float t = 0.0;
  float minDist = 999.0;
  bool hit = false;

  for (int i = 0; i < 48; i++) {
    float d = sdRoundBox(oro + ord * t, boxSize, cornerR);
    minDist = min(minDist, d);
    if (d < 0.0005) { hit = true; break; }
    t += d;
    if (t > 8.0) break;
  }

  if (!hit) {
    // Soft outer glow
    vec3 glowCol = vec3(0.3, 0.5, 0.8);
    float glow = exp(-minDist * 10.0) * 0.25;
    gl_FragColor = vec4(glowCol * glow, glow);
    return;
  }

  vec3 p = oro + ord * t;
  vec2 e = vec2(0.0005, 0.0);
  vec3 n = normalize(vec3(
    sdRoundBox(p + e.xyy, boxSize, cornerR) - sdRoundBox(p - e.xyy, boxSize, cornerR),
    sdRoundBox(p + e.yxy, boxSize, cornerR) - sdRoundBox(p - e.yxy, boxSize, cornerR),
    sdRoundBox(p + e.yyx, boxSize, cornerR) - sdRoundBox(p - e.yyx, boxSize, cornerR)
  ));

  // Determine face, compute UV, sample texture over dark base
  vec3 an = abs(n);
  vec3 darkFace = vec3(0.12);
  float imgScale = 1.2;
  vec4 tx;
  vec2 fuv;

  if (an.z >= an.x && an.z >= an.y) {
    if (n.z > 0.0) {
      fuv = (vec2(p.x + 0.5, 0.5 - p.y) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF0, clamp(fuv, 0.0, 1.0));
    } else {
      fuv = (vec2(0.5 - p.x, 0.5 - p.y) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF1, clamp(fuv, 0.0, 1.0));
    }
  } else if (an.x >= an.y) {
    if (n.x > 0.0) {
      fuv = (vec2(0.5 - p.z, 0.5 - p.y) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF2, clamp(fuv, 0.0, 1.0));
    } else {
      fuv = (vec2(p.z + 0.5, 0.5 - p.y) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF3, clamp(fuv, 0.0, 1.0));
    }
  } else {
    if (n.y > 0.0) {
      fuv = (vec2(p.x + 0.5, 0.5 - p.z) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF4, clamp(fuv, 0.0, 1.0));
    } else {
      fuv = (vec2(p.x + 0.5, p.z + 0.5) - 0.5) * imgScale + 0.5;
      tx = texture2D(uF5, clamp(fuv, 0.0, 1.0));
    }
  }

  // Mask out-of-bounds
  float inBounds = step(0.0, fuv.x) * step(fuv.x, 1.0) * step(0.0, fuv.y) * step(fuv.y, 1.0);
  vec3 color = mix(darkFace, tx.rgb, tx.a * inBounds);
  float alpha = 1.0;

  // Diffuse lighting from top-left
  vec3 wn = rot * n;
  vec3 lightDir = normalize(vec3(-0.8, 1.0, 0.5));
  float diff = max(dot(wn, lightDir), 0.0);
  color *= 0.5 + 0.5 * diff;

  // Rim glow on edges (Fresnel)
  vec3 viewDir = normalize(-ord);
  float fresnel = 1.0 - abs(dot(n, viewDir));
  fresnel = pow(fresnel, 3.0) * 0.4;
  vec3 rimCol = vec3(0.3, 0.5, 0.8);
  color += rimCol * fresnel;

  gl_FragColor = vec4(color * alpha, alpha);
}`;

// Load an image as a WebGL texture with 1x1 placeholder
function loadFaceTexture(gl, url, fallback, textureUnit) {
  const tex = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0 + textureUnit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  // 1x1 placeholder while image loads
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
    new Uint8Array(fallback));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const img = new Image();
  img.onload = () => {
    // Re-activate the correct texture unit before binding — otherwise the
    // async onload will bind this texture to whatever unit is currently
    // active, causing images to repeat on the wrong faces.
    gl.activeTexture(gl.TEXTURE0 + textureUnit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  };
  img.src = url;
  return tex;
}

// Placeholder colors per face (RGBA bytes)
const PLACEHOLDERS = [
  [54, 122, 186, 255],  // Front: blue
  [54, 122, 186, 255],  // Back: blue
  [117, 207, 235, 255], // Right: cyan
  [117, 207, 235, 255], // Left: cyan
  [255, 159, 55, 255],  // Top: orange
  [255, 159, 55, 255],  // Bottom: orange
];

export default function KuutioCubeWebGL({ size = 280, duration = 30 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      premultipliedAlpha: true,
    });
    if (!gl) return;

    function mkShader(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('Shader:', gl.getShaderInfoLog(s));
      }
      return s;
    }

    const vs = mkShader(gl.VERTEX_SHADER, VS);
    const fs = mkShader(gl.FRAGMENT_SHADER, FS);
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('Link:', gl.getProgramInfoLog(prog));
    }
    gl.useProgram(prog);

    // Uniforms
    const uTime = gl.getUniformLocation(prog, 'uTime');
    const uDuration = gl.getUniformLocation(prog, 'uDuration');
    const uRes = gl.getUniformLocation(prog, 'uRes');

    // Full-screen triangle
    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Load face textures (each bound to its own texture unit)
    const textures = FACE_IMAGES.map((url, i) =>
      loadFaceTexture(gl, url, PLACEHOLDERS[i], i)
    );

    // Set sampler uniforms (textures already bound to units 0-5 by loadFaceTexture)
    for (let i = 0; i < 6; i++) {
      gl.uniform1i(gl.getUniformLocation(prog, `uF${i}`), i);
    }

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform1f(uDuration, duration);

    const t0 = performance.now();

    function render() {
      if (!visibleRef.current) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }

      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.uniform1f(uTime, (performance.now() - t0) / 1000);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      rafRef.current = requestAnimationFrame(render);
    }

    rafRef.current = requestAnimationFrame(render);

    const observer = new IntersectionObserver(
      ([entry]) => { visibleRef.current = entry.isIntersecting; },
      { threshold: 0 }
    );
    observer.observe(canvas);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.deleteBuffer(vbo);
      textures.forEach(t => gl.deleteTexture(t));
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [size, duration]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, display: 'block' }}
    />
  );
}
